import React from "react";
import { Link } from "react-router";

import type { HttpError } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { MoreHorizontal, Plus } from "lucide-react";
import { useDebounce } from "use-debounce";

import { SearchBar } from "@/components/fields/search-bar";
import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { useEventGroupColumns } from "@/pages/event-groups/hooks/use-columns";
import {
  GroupDatesContext,
  useGroupDates,
} from "@/pages/event-groups/hooks/use-group-dates";
import {
  GROUPS_PAGE_SIZE,
  SEARCH_DEBOUNCE_MS,
  buildGroupFilters,
  groupCountLabel,
  groupExploreMeta,
  madeByTinaCount,
} from "@/pages/event-groups/lib/explore";
import type { EventGroupHit } from "@/types/event-group";

/**
 * The event groups, listed through Typesense like every other entity: the default data provider's
 * `getList` on `/event-groups/explore`, with a name search, a "Made by Tina" filter and the
 * table's own sort, filter and pager. Paging and sort stay out of the url, which belongs to the
 * events table behind the `Groups only` switch, and the list's `meta` names `query` and `queryBy`
 * itself so the events search in that url never reaches it.
 */
export const EventGroupsTable = ({
  controls,
}: {
  /** The switches the events bar carries, drawn after the search box; the view owns them. */
  controls?: React.ReactNode;
}) => {
  const columns = useEventGroupColumns();
  const [term, setTerm] = React.useState("");
  const [debouncedTerm] = useDebounce(term, SEARCH_DEBOUNCE_MS);
  const [madeByTina, setMadeByTina] = React.useState(false);

  const table = useTable<EventGroupHit, HttpError>({
    columns,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    refineCoreProps: {
      resource: "event-groups",
      syncWithLocation: false,
      meta: groupExploreMeta(debouncedTerm),
      filters: { permanent: buildGroupFilters({ madeByTina }) },
      sorters: { initial: [{ field: "updatedAt", order: "desc" }] },
      pagination: { pageSize: GROUPS_PAGE_SIZE },
      // A failed load shows below as its own message with a Retry; a toast on top would say it twice.
      errorNotification: false,
    },
  });

  // A new search or a new tick changes the set, so it never strands the table on a page past its end.
  const filterSignature = JSON.stringify([debouncedTerm.trim(), madeByTina]);
  const setCurrentPage = table.refineCore.setCurrentPage;
  const previousFilters = React.useRef(filterSignature);
  React.useEffect(() => {
    if (previousFilters.current === filterSignature) return;
    previousFilters.current = filterSignature;
    setCurrentPage(1);
  }, [filterSignature, setCurrentPage]);

  const { tableQuery } = table.refineCore;
  const answer = tableQuery.data;
  const groupDates = useGroupDates(
    (answer?.data ?? []).map((hit: EventGroupHit) => hit.document.id),
  );
  const tinaCount = madeByTinaCount(answer?.facets);

  return (
    <div className="flex flex-col gap-3">
      {/* The same bar, in the same order, as the events table's DataTableActionBar. */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-row space-x-1 items-center">
          <SearchBar
            defaultValue={term}
            handleSearch={(query) => setTerm(query)}
            suggestions={[]}
          />
          {controls}
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            className="text-foreground"
            onClick={() => setMadeByTina(false)}
          >
            Clear Filters
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-foreground items-center space-x-1"
            onClick={() => tableQuery.refetch()}
          >
            Refresh
          </Button>
          <Button asChild size="sm">
            <Link
              to="/event-groups/create"
              target="_blank"
              aria-label="Create group"
            >
              <Plus className="mr-1 h-4 w-4" />
              Create
            </Link>
          </Button>
          {/* The events bar's menu holds tag clearing and saved views, which the groups do not have. */}
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            disabled
            title="Not available for groups"
            aria-label="More actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>
        {/* Groups-only filters sit under the bar, so the bar itself keeps the events bar's width. */}
        <div className="flex flex-row items-center gap-3">
          <div className="flex items-center gap-1 whitespace-nowrap">
            <Checkbox
              id="event-groups-made-by-tina"
              checked={madeByTina}
              onCheckedChange={(checked) => setMadeByTina(checked === true)}
            />
            <Label htmlFor="event-groups-made-by-tina">
              {tinaCount === null
                ? "Made by Tina"
                : `Made by Tina (${tinaCount})`}
            </Label>
          </div>
          {/* No "0 groups" before the first answer: it would read as a fact the list does not have yet. */}
          {answer && (
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {groupCountLabel(answer.total ?? 0)}
            </span>
          )}
        </div>
      </div>

      {tableQuery.isError ? (
        // An empty table would read as "no groups"; the failure takes its place.
        <div className="flex items-center gap-3 text-sm text-destructive">
          <span>
            {toHumanErrorMessage(tableQuery.error) ||
              "Could not load the event groups."}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={tableQuery.isFetching}
            onClick={() => tableQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : (
        <GroupDatesContext.Provider value={groupDates}>
          <DataTable table={table} />
        </GroupDatesContext.Provider>
      )}
    </div>
  );
};
